import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode
} from "react";
import { Message } from "@/store/types";

/**
 * Shared feedback-modal state. Lets a message bubble deep in the chat tree
 * open the globally-rendered feedback modal seeded with a reported turn,
 * while the header button opens it with no anchor.
 */
export interface FeedbackContextValue {
  /** Whether the feedback modal is open */
  isVisible: boolean;
  /** The conversation turn the feedback is about, or null for general feedback */
  anchor: Message | null;
  /** Open the modal, optionally seeded with a reported turn */
  open: (anchor?: Message | null) => void;
  /** Close the modal and clear any anchor */
  close: () => void;
}

const noop = () => {};

const FeedbackContext = createContext<FeedbackContextValue>({
  isVisible: false,
  anchor: null,
  open: noop,
  close: noop
});

/**
 * Props for {@link FeedbackProvider}.
 */
interface FeedbackProviderProps {
  /** Subtree that can open the feedback modal and flag turns */
  children: ReactNode;
}

/**
 * Provides feedback-modal visibility and the currently reported turn to the
 * drawer subtree (header button, chat bubbles, and the modal itself).
 *
 * @param props - Component props
 * @returns Provider wrapping the drawer subtree
 */
export function FeedbackProvider({ children }: FeedbackProviderProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [anchor, setAnchor] = useState<Message | null>(null);

  const open = useCallback((next: Message | null = null) => {
    setAnchor(next);
    setIsVisible(true);
  }, []);

  // Keep the anchor while closing so it doesn't blank out mid fade-out. It is
  // always (re)set on the next open(), so a stale value is never shown.
  const close = useCallback(() => setIsVisible(false), []);

  const value = useMemo<FeedbackContextValue>(
    () => ({ isVisible, anchor, open, close }),
    [isVisible, anchor, open, close]
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}

/**
 * Access feedback-modal controls. Safe to call outside a provider (returns
 * no-op controls) so leaf components like message bubbles can be rendered in
 * isolation (e.g. tests) without crashing.
 *
 * @returns The feedback context value
 */
export function useFeedback(): FeedbackContextValue {
  return useContext(FeedbackContext);
}
